'use client';

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { TextField as AriaTextField, type TextFieldProps as AriaTextFieldProps } from 'react-aria-components/TextField';
import { cn } from '../../utils/cn';
import { Button } from '../Button/Button';
import type { IconProps } from '../Icon/types.ts';
import { IconCautionCircleStroke } from '../icons/IconCautionCircleStroke.ts';
import { IconCheckCircleStroke } from '../icons/IconCheckCircleStroke.ts';
import { InputLabel, type InputWithLabelProps } from '../../internal/inputs/InputLabel.tsx';
import { InputHelpers, type InputWithHelpersProps } from '../../internal/inputs/InputHelpers.tsx';

const iconContainerVariants = cva(
    `
        absolute top-1/2 -translate-y-1/2
        group-has-data-disabled:text-lr-icon-subtle
    `,
    {
        variants: {
            state: {
                default: '',
                success: 'text-lr-icon-success',
                error: 'text-lr-icon-error'
            },
            position: {
                left: 'pointer-events-none left-lr-spacing-150',
                right: `
                    right-lr-spacing-025
                    group-has-data-disabled:pointer-events-none
                `
            }
        },
        compoundVariants: [
            {
                state: 'default',
                position: 'left',
                className: 'group-has-data-disabled:text-lr-icon-subtle'
            }
        ],
        defaultVariants: {
            state: 'default'
        }
    }
);

/* ---------- TextBaseInput (ancien BaseInput) ---------- */

export type TextBaseInputProps<IsTextBasedInput extends boolean = true> = (IsTextBasedInput extends true
    ? AriaTextFieldProps
    : React.HTMLAttributes<HTMLDivElement> & { isRequired?: boolean; isDisabled?: boolean }) & {
    notTextFieldBasedInput?: boolean;
    children: React.ReactNode;
    hideErrorMessage?: boolean;
    showRequiredIndicator?: boolean;
    labelId?: string;
    labelHtmlFor?: string;
} & InputWithLabelProps &
    InputWithHelpersProps;

export function TextBaseInput(props: TextBaseInputProps<true> | TextBaseInputProps<false>) {
    const {
        notTextFieldBasedInput = false,
        label,
        optionalLabel,
        hint,
        indications,
        isRequired,
        isInvalid,
        isReadOnly,
        isDisabled,
        hideErrorMessage = false,
        children,
        showRequiredIndicator,
        labelId,
        labelHtmlFor,
        ...rest
    } = props as TextBaseInputProps;

    const filteredIndications = hideErrorMessage
        ? indications?.filter((indication) => indication.type !== 'error')
        : indications;

    const content = (
        <>
            <InputLabel
                label={label}
                optionalLabel={optionalLabel}
                isRequired={isRequired}
                showRequiredIndicator={showRequiredIndicator}
                labelId={labelId}
                htmlFor={labelHtmlFor}
            />

            {children}

            <InputHelpers
                hint={hint}
                indications={filteredIndications}
                wrapErrorInFieldError={!notTextFieldBasedInput}
            />
        </>
    );

    if (notTextFieldBasedInput) {
        return (
            <div
                className={cn('flex flex-col gap-lr-spacing-050')}
                data-required={isRequired}
                {...(rest as React.HTMLAttributes<HTMLDivElement>)}
            >
                {content}
            </div>
        );
    }

    return (
        <AriaTextField
            className={cn('flex w-full flex-col gap-lr-spacing-050')}
            isInvalid={isInvalid ?? !!indications?.find((indication) => indication.type === 'error')}
            isRequired={isRequired}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            {...(rest as AriaTextFieldProps)}
        >
            {content}
        </AriaTextField>
    );
}

export type TextBaseInputWithIconsProps<IsTextBasedInput extends boolean = true> =
    TextBaseInputProps<IsTextBasedInput> &
        Omit<VariantProps<typeof iconContainerVariants>, 'position'> & {
            leftIcon?: React.ReactElement<IconProps>;
            rightIcon?: React.ReactElement<IconProps>;
            state?: 'default' | 'success' | 'error';
            onRightIconPress?: () => void;
            hideStateIcon?: boolean;
        };

export function TextBaseInputWithIcons(props: TextBaseInputWithIconsProps<true> | TextBaseInputWithIconsProps<false>) {
    const {
        state = 'default',
        leftIcon,
        rightIcon,
        onRightIconPress,
        hideStateIcon = false,
        className,
        children,
        ...rest
    } = props as TextBaseInputWithIconsProps;

    const hasStateIcon = !hideStateIcon && state && state !== 'default';
    const hasRightContent = rightIcon || hasStateIcon;

    return (
        <TextBaseInput {...(rest as TextBaseInputProps)}>
            <div
                className={cn('group relative', className)}
                data-has-left-icon={leftIcon ? '' : undefined}
                data-has-right-content={hasRightContent ? '' : undefined}
            >
                {!!leftIcon && (
                    <div className={cn(iconContainerVariants({ state, position: 'left' }))}>
                        {React.cloneElement(leftIcon, {
                            ...(leftIcon.props ?? {}),
                            size: 'sm'
                        } as IconProps)}
                    </div>
                )}

                {React.isValidElement<{ className?: string }>(children)
                    ? React.cloneElement(children, {
                          ...children.props,
                          className: cn(
                              'pr-lr-spacing-025 pl-lr-spacing-150',
                              'group-data-[has-left-icon]:pl-lr-spacing-550',
                              'group-data-[has-right-content]:pr-lr-spacing-700',
                              children.props.className
                          )
                      })
                    : children}

                <div
                    className={cn(`
                        absolute top-1/2 right-lr-spacing-025 flex -translate-y-1/2 items-center gap-lr-spacing-025
                        group-has-data-[disabled]:text-lr-icon-subtle
                    `)}
                >
                    {!!hasStateIcon && (
                        <div
                            className={cn(
                                'mr-lr-spacing-125 flex items-center justify-center',
                                state === 'success' && 'text-lr-icon-success',
                                state === 'error' && 'text-lr-icon-error'
                            )}
                        >
                            {state === 'success' && <IconCheckCircleStroke size="sm" />}
                            {state === 'error' && <IconCautionCircleStroke size="sm" />}
                        </div>
                    )}

                    {!!rightIcon && (
                        <div className="group-has-data-[disabled]:pointer-events-none">
                            <Button
                                pill
                                icon
                                variant="plain"
                                className="data-[hovered]:bg-transparent data-[hovered]:shadow-none"
                                onPress={onRightIconPress}
                            >
                                {React.cloneElement(rightIcon, {
                                    ...(rightIcon.props ?? {}),
                                    size: 'sm'
                                } as IconProps)}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </TextBaseInput>
    );
}
